import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import type {
  ColumnDef,
  OnChangeFn,
  PaginationState,
  RowSelectionState,
  SortingState,
  Updater,
} from "@tanstack/react-table";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { tv } from "tailwind-variants";

import type { Pagination } from "@/api/api.types";
import { PaginationControls } from "@/ui";
import { Table } from "@/ui/table/table";
import type { RowLinkProps } from "@/ui/table/table-content";
import { PageSizeSelect } from "./page-size-select";

const paginatedTableVariants = tv({
  slots: {
    card: "overflow-hidden rounded-xl border border-line bg-surface shadow-card",
    footer:
      "flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-line px-4 py-3",
    summary: "text-sm text-content-muted",
    emphasis: "font-medium text-content",
  },
});

const { card, emphasis, footer, summary } = paginatedTableVariants();

/* Row count for the very first load, before any page size is known. */
const INITIAL_SKELETON_ROWS = 10;

export type FormErrorType = string | boolean;

export type PaginatedTableProps<TData> = {
  data: TData[] | undefined;
  pagination: Pagination | undefined;
  columns: ColumnDef<TData>[];
  setCurrentPage: (p: number) => void;
  sorting: SortingState;
  setSorting: (s: SortingState) => void;
  isLoadingData: boolean;
  rowLink?: (row: TData) => RowLinkProps;
  rowLinkLabel?: (row: TData) => string;
  footer?: ReactNode;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  getRowId?: (row: TData) => string;
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (size: number) => void;
};

export const PaginatedTable = <TData,>({
  columns,
  data,
  footer: tableFooter,
  getRowId,
  isLoadingData,
  onPageSizeChange,
  onRowSelectionChange,
  pageSizeOptions,
  pagination,
  rowLink,
  rowLinkLabel,
  rowSelection,
  setCurrentPage,
  setSorting,
  sorting,
}: PaginatedTableProps<TData>) => {
  /*
    `pagination` is undefined for the whole duration of every fetch, including
    page changes. Deriving straight off it collapsed pageSize to 0 — which made
    TableSkeleton render zero rows, so no skeleton ever appeared — and dropped
    totalPages to 0, unmounting the footer and reflowing the page mid-load.
    The last known shape is kept so the table holds its size while loading.
  */
  const [lastKnown, setLastKnown] = useState({ pageSize: 0, totalPages: 0 });

  useEffect(() => {
    if (pagination) {
      setLastKnown({ pageSize: pagination.perPage, totalPages: pagination.totalPages });
    }
  }, [pagination]);

  /* The page the user just asked for, shown as current until the data lands. */
  const [requestedPage, setRequestedPage] = useState<number | null>(null);

  const [requestedPageSize, setRequestedPageSize] = useState<number | null>(null);

  const totalPages = pagination?.totalPages ?? lastKnown.totalPages;
  const currentPage = pagination?.currentPage ?? requestedPage ?? 1;
  const pageSize = pagination?.perPage ?? lastKnown.pageSize;
  const selectedPageSize = pagination?.perPage ?? requestedPageSize ?? lastKnown.pageSize;
  const skeletonRows = pageSize || INITIAL_SKELETON_ROWS;

  const handlePaginationChange = (updater: Updater<PaginationState>) => {
    if (updater instanceof Function) {
      const params = updater({ pageIndex: currentPage - 1, pageSize });
      const nextPage = params.pageIndex + 1;

      setRequestedPage(nextPage);
      setCurrentPage(nextPage);
    }
  };

  const handleSortChange = (updater: Updater<SortingState>) => {
    if (updater instanceof Function) {
      const params = updater(sorting);
      setSorting(params);
    }
  };

  const table = useReactTable({
    data: data ?? [],
    columns,
    manualPagination: true,
    manualSorting: true,
    enableSortingRemoval: true,
    enableRowSelection: Boolean(onRowSelectionChange),
    pageCount: pagination?.total,
    state: {
      pagination: { pageIndex: currentPage - 1, pageSize },
      sorting,
      ...(rowSelection ? { rowSelection } : {}),
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortChange,
    onRowSelectionChange,
  });

  const total = pagination?.total ?? 0;
  const firstOnPage = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastOnPage = Math.min(currentPage * pageSize, total);

  const renderSummary = () => {
    if (isLoadingData) {
      return "Loading…";
    }

    if (total === 0) {
      return "No results";
    }

    return (
      <>
        Showing{" "}
        <span className={emphasis()}>
          {firstOnPage}–{lastOnPage}
        </span>{" "}
        of <span className={emphasis()}>{total}</span>
      </>
    );
  };

  return (
    /*
      The table and its footer are one card, not two bordered boxes: tailwind-merge
      does not collapse utilities across breakpoint variants, so a `sm:rounded-lg`
      on the table would re-round corners a `rounded-b-none` had removed.
    */
    <div className={card()}>
      <Table
        card={false}
        footer={tableFooter}
        isLoadingData={isLoadingData}
        rowLink={rowLink}
        rowLinkLabel={rowLinkLabel}
        rowsPerPage={skeletonRows}
        table={table}
      />

      {!!totalPages && (
        <div className={footer()}>
          <p className={summary()}>{renderSummary()}</p>

          <div className="flex items-center gap-4">
            {pageSizeOptions && onPageSizeChange ? (
              <PageSizeSelect
                onChange={(size) => {
                  setRequestedPageSize(size);
                  onPageSizeChange(size);
                }}
                options={pageSizeOptions}
                value={selectedPageSize}
              />
            ) : null}

            <PaginationControls
              currentPage={currentPage}
              numPages={totalPages}
              setPage={(i: number) => {
                return table.setPageIndex(i - 1);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
PaginatedTable.displayName = "PaginatedTable";
